import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Divider,
  Toolbar,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  MoreVert,
  CheckCircle,
  Psychology,
  Science,
  Book,
  Public,
  TrendingUp,
  SmartToy,
  Verified,
  QuestionMark,
  ErrorOutline,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { adminService, type AdminQuestion } from '../services/adminService';



interface GradeStats {
  _id: number;
  total: number;
  aiGenerated: number;
  verified: number;
  subjects: string[];
  avgDifficulty: number;
  recentlyAdded: number;
}

interface SubjectStats {
  _id: string;
  total: number;
  aiGenerated: number;
  verified: number;
  avgDifficulty: number;
  recentlyAdded: number;
}

interface QuestionFilters {
  subject?: string;
  grade?: number;
  difficulty?: number;
  aiGenerated?: boolean;
  verified?: boolean;
  search?: string;
}

const AdminQuestions: React.FC = () => {
  const theme = useTheme();
  const [view, setView] = useState<'grades' | 'subjects' | 'browser'>('grades');
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [gradeStats, setGradeStats] = useState<GradeStats[]>([]);
  const [subjectStats, setSubjectStats] = useState<SubjectStats[]>([]);
  const [questions, setQuestions] = useState<AdminQuestion[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<QuestionFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // AI generation progress (live)
  const [generating, setGenerating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genGrade, setGenGrade] = useState<number | null>(null);
  const [genSteps, setGenSteps] = useState<{ subject: string; status: 'pending' | 'running' | 'done' | 'failed'; count?: number; error?: string }[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (view === 'grades') {
      fetchGradeStats();
    } else if (view === 'subjects') {
      fetchSubjectStats();
    } else {
      fetchQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedGrade, filters, pagination.page]);

  const fetchGradeStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getQuestionStats();
      setGradeStats(response.data.byGrade || []);
    } catch (err) {
      setError('Failed to fetch grade statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjectStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getQuestionStats(selectedGrade || undefined);
      setSubjectStats(response.data.bySubject || []);
    } catch (err) {
      setError('Failed to fetch subject statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getQuestions({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        search: searchQuery,
      });
      setQuestions(response.data.questions || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.totalQuestions || 0,
        totalPages: response.data.pagination?.totalPages || 0,
      }));
    } catch (err) {
      setError('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, searchQuery]);

  const handleGradeClick = useCallback((grade: number) => {
    setSelectedGrade(grade);
    setFilters({ grade });
    setError(null);
    setSuccessMessage(null);
    setView('subjects');
  }, []);

  const handleSubjectClick = useCallback((subject: string) => {
    setFilters({ subject, grade: selectedGrade || undefined });
    setView('browser');
  }, [selectedGrade]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // Debounce search in real implementation
    setTimeout(() => {
      if (view === 'browser') {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchQuestions();
      }
    }, 300);
  }, [view]);

  const handleFilterChange = useCallback((newFilters: Partial<QuestionFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchQuestions();
  }, [fetchQuestions]);

  const handleQuestionSelect = (questionId: string) => {
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedQuestions.size === questions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions.map(q => q._id)));
    }
  };

  const handleBulkAction = async (action: 'delete' | 'verify' | 'activate' | 'deactivate') => {
    try {
      const questionIds = Array.from(selectedQuestions);
      // Implement bulk actions
      console.log(`Bulk ${action} for questions:`, questionIds);
      setSelectedQuestions(new Set());
      fetchQuestions();
    } catch (err) {
      setError(`Failed to ${action} questions`);
    }
  };

  const handleAddQuestion = () => {
    // TODO: Implement add question dialog
    console.log('Add question for grade:', selectedGrade);
    setError('Add Question feature coming soon');
  };

  const handleGenerateQuestions = async (specificSubject?: string) => {
    if (!selectedGrade) {
      setError('Please select a grade first');
      return;
    }

    const subjects = specificSubject ? [specificSubject] : ['math', 'science', 'english', 'gk'];
    const questionsPerSubject = 10;

    setError(null);
    setSuccessMessage(null);
    setGenGrade(selectedGrade);
    setGenSteps(subjects.map((s) => ({ subject: s, status: 'pending' as const })));
    setGenerating(true);
    setGenOpen(true);

    let successCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < subjects.length; i++) {
      const subject = subjects[i];
      // mark this subject as running (live update)
      setGenSteps((prev) => prev.map((st, idx) => (idx === i ? { ...st, status: 'running' } : st)));
      try {
        const response = await adminService.generateQuestions({
          subject,
          grade: selectedGrade,
          count: questionsPerSubject,
        });
        const made = response?.data?.questionsGenerated ?? questionsPerSubject;
        setGenSteps((prev) => prev.map((st, idx) => (idx === i ? { ...st, status: 'done', count: made } : st)));
        successCount++;
      } catch (subjectError) {
        const msg = subjectError instanceof Error ? subjectError.message : 'Unknown error';
        setGenSteps((prev) => prev.map((st, idx) => (idx === i ? { ...st, status: 'failed', error: msg } : st)));
        errors.push(`${subject}: ${msg}`);
      }
    }

    // Refresh stats now that generation is done
    try {
      await fetchGradeStats();
      if (view === 'subjects') await fetchSubjectStats();
    } catch (refreshError) {
      console.error('Failed to refresh stats after generation:', refreshError);
    }

    setGenerating(false);

    if (successCount > 0) {
      setSuccessMessage(
        specificSubject
          ? `Generated questions for ${specificSubject}, Grade ${selectedGrade}.`
          : `Generated questions across ${successCount}/${subjects.length} subjects for Grade ${selectedGrade}.`
      );
      setTimeout(() => setSuccessMessage(null), 8000);
    }
    if (errors.length > 0) {
      setError(`Some subjects failed: ${errors.join(', ')}`);
      setTimeout(() => setError(null), 8000);
    }
  };

  const getSubjectIcon = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'math': return <Psychology sx={{ fontSize: 40 }} />;
      case 'science': return <Science sx={{ fontSize: 40 }} />;
      case 'english': return <Book sx={{ fontSize: 40 }} />;
      case 'gk': return <Public sx={{ fontSize: 40 }} />;
      default: return <QuestionMark sx={{ fontSize: 40 }} />;
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'math': return theme.palette.primary.main;
      case 'science': return theme.palette.secondary.main;
      case 'english': return theme.palette.success.main;
      case 'gk': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 2) return theme.palette.success.main;
    if (difficulty <= 4) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getDifficultyLabel = (difficulty: number) => {
    if (difficulty <= 2) return 'Easy';
    if (difficulty <= 4) return 'Medium';
    return 'Hard';
  };

  const renderGradeOverview = () => (
    <Box sx={{ px: 2, py: 1 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Quick Stats */}
        <Box sx={{ mb: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Quick Statistics
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {gradeStats.reduce((sum, g) => sum + g.total, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Questions
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {gradeStats.reduce((sum, g) => sum + g.aiGenerated, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI Generated
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {gradeStats.reduce((sum, g) => sum + g.verified, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verified
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {gradeStats.reduce((sum, g) => sum + g.recentlyAdded, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recently Added
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Grade Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 4,
          }}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((grade) => {
            const gradeData = gradeStats.find(g => g._id === grade);
            const totalQuestions = gradeData?.total || 0;
            const aiGenerated = gradeData?.aiGenerated || 0;
            const verified = gradeData?.verified || 0;
            const recentlyAdded = gradeData?.recentlyAdded || 0;
            
            return (
              <motion.div
                key={grade}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              >
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    minHeight: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 8,
                    },
                  }}
                  onClick={() => handleGradeClick(grade)}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {grade}
                      </Avatar>
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Grade {grade}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {totalQuestions} Questions
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        size="small"
                        label={`${aiGenerated} AI`}
                        sx={{
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${verified} Verified`}
                        sx={{
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Subjects: {gradeData?.subjects.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg Difficulty: {gradeData?.avgDifficulty?.toFixed(1) || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main, mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          +{recentlyAdded}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </Box>


      </motion.div>
    </Box>
  );

  const renderSubjectOverview = () => (
    <Box sx={{ px: 2, py: 1 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedGrade(null);
              setError(null);
              setView('grades');
            }}
            sx={{ mr: 2 }}
          >
            ← Back to Grades
          </Button>
          <Typography variant="h6">
            Grade {selectedGrade} Subjects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            ({subjectStats.reduce((sum, s) => sum + s.total, 0)} total questions)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddQuestion}
            >
              Add Question
            </Button>
            <Button
              variant="outlined"
              startIcon={generating ? <CircularProgress size={16} /> : <SmartToy />}
              onClick={() => handleGenerateQuestions()}
              disabled={generating}
              title="Generate 10 questions for each subject (Math, Science, English, GK)"
            >
              {generating ? 'Generating…' : 'Generate All (+40)'}
            </Button>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Box sx={{ mb: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Quick Statistics
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {subjectStats.reduce((sum, s) => sum + s.total, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Questions
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {subjectStats.reduce((sum, s) => sum + s.aiGenerated, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI Generated
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {subjectStats.reduce((sum, s) => sum + s.verified, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verified
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {subjectStats.reduce((sum, s) => sum + s.recentlyAdded, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recently Added
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Subject Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {subjectStats.map((subject) => (
            <motion.div
              key={subject._id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: `linear-gradient(135deg, ${getSubjectColor(subject._id)}15 0%, ${getSubjectColor(subject._id)}05 100%)`,
                  border: `1px solid ${alpha(getSubjectColor(subject._id), 0.1)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 32px ${alpha(getSubjectColor(subject._id), 0.2)}`,
                  },
                }}
                onClick={() => handleSubjectClick(subject._id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        backgroundColor: alpha(getSubjectColor(subject._id), 0.1),
                        color: getSubjectColor(subject._id),
                        mr: 2,
                      }}
                    >
                      {getSubjectIcon(subject._id)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {subject._id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subject.total} questions
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      icon={<SmartToy />}
                      label={`${subject.aiGenerated} AI`}
                      sx={{
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                      }}
                    />
                    <Chip
                      size="small"
                      icon={<Verified />}
                      label={`${subject.verified} Verified`}
                      sx={{
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Grade: {selectedGrade}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Difficulty: {subject.avgDifficulty.toFixed(1)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main, mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        +{subject.recentlyAdded}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Generate Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={generating ? <CircularProgress size={14} /> : <SmartToy />}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleGenerateQuestions(subject._id);
                      }}
                      disabled={generating}
                      title={`Generate 10 ${subject._id} questions for Grade ${selectedGrade} - Takes 30-60 seconds`}
                      sx={{
                        borderColor: getSubjectColor(subject._id),
                        color: getSubjectColor(subject._id),
                        '&:hover': {
                          borderColor: getSubjectColor(subject._id),
                          backgroundColor: alpha(getSubjectColor(subject._id), 0.05),
                        },
                      }}
                    >
                      {loading ? 'Generating...' : 'Generate +10'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>


      </motion.div>
    </Box>
  );

  const renderQuestionBrowser = () => (
    <Box sx={{ px: 2, py: 1 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setView('subjects')}
            sx={{ mr: 2 }}
          >
            ← Back to Subjects
          </Button>
          <Typography variant="h6">
            {filters.subject ? `${filters.subject.charAt(0).toUpperCase() + filters.subject.slice(1)} Questions` : 'All Questions'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            ({pagination.total} total)
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search questions..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Grade</InputLabel>
              <Select
                value={filters.grade || ''}
                onChange={(e) => handleFilterChange({ grade: e.target.value as number })}
                label="Grade"
              >
                <MenuItem value="">All Grades</MenuItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(grade => (
                  <MenuItem key={grade} value={grade}>Grade {grade}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={filters.difficulty || ''}
                onChange={(e) => handleFilterChange({ difficulty: e.target.value as number })}
                label="Difficulty"
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value={1}>Easy</MenuItem>
                <MenuItem value={3}>Medium</MenuItem>
                <MenuItem value={5}>Hard</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            >
              More Filters
            </Button>
          </Box>
        </Paper>

        {/* Bulk Actions Toolbar */}
        {selectedQuestions.size > 0 && (
          <Toolbar
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              {selectedQuestions.size} selected
            </Typography>
            <Button
              startIcon={<CheckCircle />}
              onClick={() => handleBulkAction('verify')}
              sx={{ mr: 1 }}
            >
              Verify
            </Button>
            <Button
              startIcon={<Delete />}
              onClick={() => handleBulkAction('delete')}
              color="error"
            >
              Delete
            </Button>
            <IconButton
              onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
              sx={{ ml: 1 }}
            >
              <MoreVert />
            </IconButton>
          </Toolbar>
        )}

        {/* Question List */}
        <Paper>
          <List>
            {/* Select All */}
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedQuestions.size === questions.length && questions.length > 0}
                  indeterminate={selectedQuestions.size > 0 && selectedQuestions.size < questions.length}
                  onChange={handleSelectAll}
                />
              </ListItemIcon>
              <ListItemText
                primary="Select All"
                secondary={`${questions.length} questions on this page`}
              />
            </ListItem>
            <Divider />

            {questions.map((question, index) => (
              <React.Fragment key={question._id}>
                <ListItem
                  sx={{
                    alignItems: 'flex-start',
                    py: 3,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    <Checkbox
                      checked={selectedQuestions.has(question._id)}
                      onChange={() => handleQuestionSelect(question._id)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ mb: 1 }}>
                        {/* Question Header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                              Q. {question.questionText}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={`Grade ${question.grade}`}
                              sx={{ fontSize: '0.75rem' }}
                            />
                            <Chip
                              size="small"
                              label={getDifficultyLabel(question.difficultyLevel)}
                              sx={{
                                backgroundColor: alpha(getDifficultyColor(question.difficultyLevel), 0.1),
                                color: getDifficultyColor(question.difficultyLevel),
                                fontSize: '0.75rem',
                              }}
                            />
                            {question.aiGenerated && (
                              <Chip
                                size="small"
                                label="AI"
                                sx={{
                                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main,
                                  fontSize: '0.75rem',
                                }}
                              />
                            )}
                            {question.verified && (
                              <Chip
                                size="small"
                                label="Verified"
                                sx={{
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  color: theme.palette.success.main,
                                  fontSize: '0.75rem',
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Options */}
                        {question.options && question.options.length > 0 && (
                          <Box sx={{ mb: 2, pl: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                              Options:
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                              {question.options.map((option, optIndex) => {
                                const optionLabel = String.fromCharCode(65 + optIndex); // A, B, C, D
                                // Check both letter-based (A, B, C, D) and text-based answers
                                const isCorrect = question.correctAnswer === optionLabel || question.correctAnswer === option;
                                return (
                                  <Box
                                    key={optIndex}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      p: 1,
                                      borderRadius: 1,
                                      backgroundColor: isCorrect 
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.grey[300], 0.3),
                                      border: isCorrect 
                                        ? `1px solid ${alpha(theme.palette.success.main, 0.5)}`
                                        : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                    }}
                                  >
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: isCorrect ? 'bold' : 'normal',
                                        color: isCorrect ? theme.palette.success.main : 'inherit'
                                      }}
                                    >
                                      {optionLabel}. {option}
                                      {isCorrect && ' ✓'}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Explanation */}
                        {question.explanation && (
                          <Box sx={{ mb: 2, pl: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                              Explanation:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {question.explanation}
                            </Typography>
                          </Box>
                        )}

                        {/* Metadata */}
                        <Box sx={{ pl: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {question.topic} • {question.questionType.replace('_', ' ')} • 
                            Asked {question.stats.timesAsked} times • 
                            {question.stats.correctAnswers}/{question.stats.timesAsked} correct
                            {question.stats.timesAsked > 0 && 
                              ` (${Math.round((question.stats.correctAnswers / question.stats.timesAsked) * 100)}% success rate)`
                            }
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction sx={{ top: 16 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => console.log('Edit question:', question._id)}
                        title="Edit question"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => console.log('More actions:', question._id)}
                        title="More actions"
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < questions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Button
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              sx={{ mr: 1 }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ mx: 2, alignSelf: 'center' }}>
              Page {pagination.page} of {pagination.totalPages}
            </Typography>
            <Button
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              sx={{ ml: 1 }}
            >
              Next
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );

  if (loading && (view === 'grades' || view === 'subjects')) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Live AI-generation progress */}
      <Dialog open={genOpen} maxWidth="sm" fullWidth>
        <DialogTitle>
          {generating ? `Generating questions — Grade ${genGrade}` : 'Generation complete'}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const total = genSteps.length || 1;
            const finished = genSteps.filter((s) => s.status === 'done' || s.status === 'failed').length;
            const made = genSteps.reduce((a, s) => a + (s.count || 0), 0);
            const pct = Math.round((finished / total) * 100);
            const label = (subj: string) =>
              subj === 'gk' ? 'General Knowledge' : subj.charAt(0).toUpperCase() + subj.slice(1);
            return (
              <>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {finished}/{genSteps.length} subjects · {made} questions added
                </Typography>
                <List dense>
                  {genSteps.map((s) => (
                    <ListItem key={s.subject} disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {s.status === 'running' ? (
                          <CircularProgress size={20} />
                        ) : s.status === 'done' ? (
                          <CheckCircle color="success" />
                        ) : s.status === 'failed' ? (
                          <ErrorOutline color="error" />
                        ) : (
                          <RadioButtonUnchecked color="disabled" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={label(s.subject)}
                        secondary={
                          s.status === 'running'
                            ? 'Writing & self-checking questions…'
                            : s.status === 'done'
                            ? `Added ${s.count ?? 0} questions`
                            : s.status === 'failed'
                            ? s.error || 'Failed'
                            : 'Waiting…'
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {generating && (
                  <Typography variant="caption" color="text.secondary">
                    Each subject takes ~20–40s — gpt-4.1 writes each question and self-checks the answer. Please keep this open.
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setGenOpen(false); setGenSteps([]); }}
            disabled={generating}
            variant={generating ? 'text' : 'contained'}
          >
            {generating ? 'Working…' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {view === 'grades' ? renderGradeOverview() : 
       view === 'subjects' ? renderSubjectOverview() : 
       renderQuestionBrowser()}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleFilterChange({ aiGenerated: true })}>
          AI Generated Only
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ verified: true })}>
          Verified Only
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange({ verified: false })}>
          Unverified Only
        </MenuItem>
        <MenuItem onClick={() => { setFilters({}); setFilterMenuAnchor(null); }}>
          Clear Filters
        </MenuItem>
      </Menu>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkMenuAnchor}
        open={Boolean(bulkMenuAnchor)}
        onClose={() => setBulkMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkAction('activate')}>
          Activate Selected
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('deactivate')}>
          Deactivate Selected
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminQuestions; 